// utils.ts

/**
 * Creates a dropdown element with a specific className.
 * @param className - The class to add to the dropdown element.
 * @returns The created dropdown element.
 */
export const createDropdownElement = (className: string): HTMLDivElement => {
  const dropdown = document.createElement("div");
  dropdown.classList.add(className);
  dropdown.style.display = "none"; // Initially hidden
  return dropdown;
};

/**
 * Filters the dropdown options based on the query string.
 * @param dropdown - The dropdown element to populate.
 * @param query - The query string to filter options by.
 * @param options - Array of string options to display in the dropdown.
 */
export const filterDropdownOptions = (
  dropdown: HTMLDivElement,
  query: string,
  options: string[],
  handleTimeOptionClick: (option: string) => void,
  closeDropdown?: () => void
): void => {
  dropdown.innerHTML = ""; // Clear previous options

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(query.toLowerCase())
  );

  // Populate dropdown with matching options
  if (filteredOptions.length > 0) {
    filteredOptions.forEach((option) => {
      const optionElement = document.createElement("span");
      optionElement.classList.add("dropdown-option");
      optionElement.textContent = option;
      optionElement.addEventListener('click', () => { handleTimeOptionClick(option) })
      dropdown.appendChild(optionElement);
    });
    toggleDropdownVisibility(dropdown, true); // Show the dropdown
  } else {
    if (closeDropdown) {
      closeDropdown();
    }
    toggleDropdownVisibility(dropdown, false); // Hide the dropdown if no match
  }
};

/**
 * Positions the dropdown relative to the input field.
 * @param dropdown - The dropdown element to position.
 * @param inputElement - The input element to base the dropdown positioning on.
 * @param wrapperElement - The wrapper element to base the dropdown positioning on. This will take precedence over the input element.
 * @param dropdownPosition - The position of the dropdown. 'fixed' or 'absolute'.
 */
export const positionDropdownRelativeToInput = (
  dropdown: HTMLDivElement,
  inputElement: HTMLInputElement,
  wrapperElement?: HTMLElement,
  dropdownPosition: 'fixed' | 'absolute' = 'absolute'
): void => {
  dropdown.style.position = dropdownPosition;
  dropdown.style.left = '0';
  dropdown.style.width = '100%';
  dropdown.style.zIndex = '100';

  // Calculate available space below and above
  const wrapperRect = wrapperElement?.getBoundingClientRect() || inputElement.getBoundingClientRect();
  const dropdownHeight = dropdown.offsetHeight;
  const windowHeight = window.innerHeight;
  const spaceBelow = windowHeight - wrapperRect.bottom;
  const spaceAbove = wrapperRect.top;

  // Position above if not enough space below and more space above
  if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
    dropdown.style.bottom = '110%';
    dropdown.style.top = 'auto';
  } else {
    dropdown.style.top = '110%';
    dropdown.style.bottom = 'auto';
  }
};

/**
 * Formats a Date object into a time string (HH:MM AM/PM).
 * @param date - The Date object to format.
 * @returns The formatted time string (e.g., "12:00 PM").
 */
export const formatTime = (date: Date): string =>
  date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

/**
 * Toggles the visibility of a dropdown element.
 * @param dropdown - The dropdown element to show or hide.
 * @param isVisible - A boolean indicating whether the dropdown should be visible.
 */
export const toggleDropdownVisibility = (
  dropdown: HTMLDivElement,
  isVisible: boolean
): void => {
  dropdown.style.display = isVisible ? "flex" : "none";
};

// Generates the time options based on minute step
export const generateTimeOptions = (minuteStep: number): string[] => {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += minuteStep) {
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      const formattedTime = formatTime(date);
      times.push(formattedTime);
    }
  }
  return times;
};

/**
 * Parses a time input string and returns a formatted time string.
 * Handles various input formats and edge cases including AM/PM specifications.
 * Hours greater than 24 are handled by taking remainder of 24.
 * 
 * @param input - The time input string to parse (can be numeric, time string, or include AM/PM)
 * @returns A formatted time string in "HH:MM AM/PM" format
 * 
 * @example
 * parseTimeInput("1") => "1:00 AM"
 * parseTimeInput("abc") => "12:00 AM"
 * parseTimeInput("3456") => "12:00 AM"
 * parseTimeInput("1:30") => "1:30 AM"
 * parseTimeInput("1:30 PM") => "1:30 PM"
 * parseTimeInput("13:45") => "1:45 PM"
 */
export const parseTimeInput = (input: string): string => {
  // Clean and standardize the input
  const cleanInput = input.trim().toUpperCase();
  if (!cleanInput) return "12:00 AM"; // Default to "12:00 AM" if input is empty

  // Check if the input specifies AM or PM
  const hasAMPM = /\s*(AM|PM)$/i.test(cleanInput);
  let specifiedPeriod = hasAMPM ? cleanInput.slice(-2) : ''; // Extract AM/PM if present
  const timeString = hasAMPM ? cleanInput.slice(0, -2).trim() : cleanInput; // Remove AM/PM for parsing

  // Helper function to format time
  const parseTime = (hours: number, minutes: number): string => {
    // Validate hours and minutes
    if (isNaN(hours) || isNaN(minutes) || hours >= 24 || minutes >= 60) {
      return "12:00 AM"; // Return default if invalid
    }
    // Determine AM/PM if not specified
    if (!specifiedPeriod) {
      specifiedPeriod = hours >= 12 ? 'PM' : 'AM';
    }
    // Convert 24-hour format to 12-hour format
    hours = hours % 12 || 12;
    const minuteString = minutes.toString().padStart(2, '0'); // Pad minutes to ensure 2 digits
    return `${hours}:${minuteString} ${specifiedPeriod}`;
  };

  // Handle time strings with colon (e.g., "1:30")
  if (timeString.includes(':')) {
    const [hoursStr, minutesStr] = timeString.split(':');
    return parseTime(parseInt(hoursStr), parseInt(minutesStr));
  }

  // Handle numeric inputs (e.g., "130" for "1:30")
  const numericInput = timeString.replace(/[^0-9]/g, '');
  if (!numericInput) return "12:00 AM"; // Default if no numeric input

  // Handle inputs with 3 or more digits (e.g., "1345" for "13:45")
  if (numericInput.length >= 3) {
    const paddedInput = numericInput.padStart(4, '0'); // Pad to ensure at least 4 digits
    const hours = parseInt(paddedInput.substring(0, 2));
    const minutes = parseInt(paddedInput.substring(2, 4));
    return parseTime(hours, minutes);
  }

  // Handle inputs with 1 or 2 digits (e.g., "1" for "1:00")
  const hours = parseInt(numericInput.substring(0, 2));
  return parseTime(hours, 0);
};